// ./src/hooks/useCommandPalette.ts
//
// A tiny module-level registry that lets any page contribute contextual commands
// to the global ⌘K command palette, without prop drilling or caring where it is
// mounted in the tree. Pages call usePageCommands(...) to register/unregister
// their commands while mounted; the CommandPalette component subscribes via
// usePaletteCommands() and renders them.

import { useEffect, useRef } from "react";
import { useSyncExternalStore } from "react";

// ============================================
// Types
// ============================================

export interface PaletteCommand {
  /** Stable id, unique within the page's command set. */
  id: string;
  /** Command label shown in the palette. */
  label: string;
  /** Optional secondary description. */
  description?: string;
  /** Optional Tabler icon name (AppIcon). */
  icon?: string;
  /** Extra search terms. */
  keywords?: string[];
  /** Invoked when the command is chosen. */
  onClick: () => void;
}

// ============================================
// Registry (module-level pub/sub)
// ============================================

interface Entry {
  ownerId: string;
  commands: PaletteCommand[];
}

let entries: Entry[] = [];
let snapshot: PaletteCommand[] = [];
const listeners = new Set<() => void>();

const recompute = () => {
  snapshot = entries.flatMap((e) => e.commands);
};

const emit = () => {
  listeners.forEach((listener) => listener());
};

const setCommands = (ownerId: string, commands: PaletteCommand[]) => {
  entries = entries.filter((e) => e.ownerId !== ownerId);

  if (commands.length > 0) {
    entries.push({ ownerId, commands });
  }

  recompute();
  emit();
};

const clearCommands = (ownerId: string) => {
  const before = entries.length;
  entries = entries.filter((e) => e.ownerId !== ownerId);

  if (entries.length !== before) {
    recompute();
    emit();
  }
};

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
};

let ownerCounter = 0;

// ============================================
// Hooks
// ============================================

/**
 * Registers a page's contextual commands into the global command palette while
 * the component is mounted. Memoize `commands` in the caller so it isn't
 * re-registered on every render. Pass `enabled: false` to temporarily withdraw
 * them (e.g. while a modal owns the screen).
 */
export const usePageCommands = (
  commands: PaletteCommand[],
  enabled: boolean = true
): void => {
  const ownerIdRef = useRef<string>("");

  if (!ownerIdRef.current) {
    ownerCounter += 1;
    ownerIdRef.current = `owner-${ownerCounter}`;
  }

  useEffect(() => {
    const ownerId = ownerIdRef.current;

    if (!enabled) {
      clearCommands(ownerId);

      return undefined;
    }

    setCommands(ownerId, commands);

    return () => {
      clearCommands(ownerId);
    };
  }, [commands, enabled]);
};

/** Live list of all currently registered page commands (for the palette). */
export const usePaletteCommands = (): PaletteCommand[] =>
  useSyncExternalStore(subscribe, () => snapshot);
